import { useEffect, useState } from 'react'
import useBackendClient from '~/api/BackendClient/useBackendClient'
import {
  MonitorParseManualPassageDataRequest,
  MonitorParseManualPassageDataResponse,
  MonitorParseManualPassageDataResponseWorklists,
} from '~/api/backend/pages/wellPageAPI'
import Button from '~/components/buttons/Button'
import MinimalButton from '~/components/buttons/MinimalButton'
import Textarea from '~/components/Textarea'
import Toaster from '~/components/Toaster'
import useDebouncedAPIEndpoint from '~/utils/hooks/useDebouncedAPIEndpoint'
import { passageQueue } from '~/utils/PassageQueue'
import RecordManualPassageDataSchemasDialog from './RecordManualPassageDataSchemasDialog'
import UploadDataStepPreview from './UploadDataStepPreview'
import cs from './upload_data_step.scss'

const UploadDataStep = () => {
  const client = useBackendClient()
  const [dataSchemasDialogOpen, setDataSchemasDialogOpen] = useState(false)
  const {
    response: parseDataDebouncedResponse,
    debouncedAPIEndpoint: debouncedParseData,
    loading: isParsingData,
  } = useDebouncedAPIEndpoint<
    MonitorParseManualPassageDataRequest,
    MonitorParseManualPassageDataResponse
  >(async (parseDataRequest: MonitorParseManualPassageDataRequest) => {
    return client.wellPage.parseManualPassageData({
      data: parseDataRequest.data,
    })
  }, 250)
  const [rawData, setRawData] = useState('')
  const [parseDataResponse, setParseDataResponse] =
    useState<MonitorParseManualPassageDataResponse | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (parseDataDebouncedResponse === null) {
      setParseDataResponse(null)
    } else if (parseDataDebouncedResponse.type === 'response') {
      setParseDataResponse(parseDataDebouncedResponse.response)
    } else if (parseDataDebouncedResponse.type === 'error') {
      Toaster.show({
        message: 'Error communicating with the server.',
        intent: 'danger',
      })
      setParseDataResponse(null)
    }
  }, [parseDataDebouncedResponse])

  useEffect(() => {
    // Null responses must be passed through the debounced API call, so
    // they don't get overwritten by stale responses.
    debouncedParseData(rawData ? { data: rawData } : null)
  }, [rawData])

  const handleSave = async () => {
    if (!parseDataResponse || parseDataResponse.type !== 'worklists') {
      return
    }

    setIsSaving(true)
    let results: PromiseSettledResult<void>[] = []

    try {
      // Process worklists by queuing them for sequential processing
      // This is to avoid overwhelming the server with too many requests at once
      const response =
        parseDataResponse as MonitorParseManualPassageDataResponseWorklists
      const promises = response.data.map(async worklist => {
        const wellTransfers: [string, string][] = worklist.well_transfers.map(
          transfer => [transfer.parent_well, transfer.well] as [string, string],
        )

        try {
          await passageQueue.enqueue(
            {
              parent_plate_name: worklist.parent_plate_barcode,
              child_plate_name: worklist.plate_barcode,
              source_well_to_dest_well_transfers: wellTransfers,
            },
            client.wellPage.recordManualPassage,
          )
        } catch (error) {
          console.error(
            `Failed to queue passage for ${worklist.parent_plate_barcode} -> ${worklist.plate_barcode}:`,
            error,
          )
          throw error
        }
      })

      results = (await Promise.allSettled(promises)) as PromiseSettledResult<void>[]

      const failures = results.filter(r => r.status === 'rejected')
      if (failures.length == 0) {
        Toaster.show({
          message: `Successfully recorded ${results.length} manual passages`,
          intent: 'success',
        })
      } else if (failures.length < results.length) {
        Toaster.show({
          message: `Successfully recorded ${results.length - failures.length} manual passages. ${failures.length} failed.`,
          intent: 'warning',
        })
      } else {
        Toaster.show({
          message: 'All manual passages failed to record',
          intent: 'danger',
        })
      }
    } catch (error) {
      Toaster.show({
        message: `Error queuing manual passages: ${error instanceof Error ? error.message : 'Unknown error'}`,
        intent: 'danger',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className={cs.uploadDataStep}>
      <div className={cs.header}>
        <div className={cs.headerText}>Manual Passage Plates</div>
        <div className={cs.fill} />
        <Button className={cs.cancelButton} label='Cancel' to='/cultures' />
        <Button
          className={cs.button}
          label={isSaving ? 'Saving...' : 'Save'}
          onClick={handleSave}
          disabled={
            isSaving || !parseDataResponse || parseDataResponse.type !== 'worklists'
          }
        />
      </div>
      <div className={cs.body}>
        <div className={cs.inputColumn}>
          <div className={cs.sectionTitle}>Paste Manual Passage Data</div>
          <Textarea
            className={cs.textarea}
            rows={8}
            onChange={setRawData}
            value={rawData}
          />
          <MinimalButton
            className={cs.viewAcceptedDataSchemas}
            type='primary'
            onClick={() => setDataSchemasDialogOpen(true)}
            label='View Accepted Data Formats'
            variant='normal'
          />
        </div>
        <div className={cs.previewColumn}>
          <UploadDataStepPreview
            className={cs.preview}
            loading={isParsingData}
            parseDataResponse={parseDataResponse}
          />
        </div>
        <RecordManualPassageDataSchemasDialog
          isOpen={dataSchemasDialogOpen}
          onClose={() => setDataSchemasDialogOpen(false)}
        />
      </div>
    </div>
  )
}

export default UploadDataStep
